import type { CurrentSession } from '@elite-residence/api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useApiClient } from '../api/ApiClientProvider';
import { type Dictionary, useLocale, useText } from '../i18n/LocaleProvider';
import { Alert, Badge, Field, Loading, QueryFailure, readResponse, useAction } from '../ui/ui';

// Property Setup (V1-2-01 to V1-2-04, V1-2-06): Branches, Floors and Units.
// Exact values (Unit Numbers, reference prices) are shown as received; the
// Monthly Reference Price is a reference only and never a Booking price.

const text: Dictionary<
  | 'property'
  | 'branches'
  | 'code'
  | 'name'
  | 'addBranch'
  | 'create'
  | 'archived'
  | 'operational'
  | 'open'
  | 'floors'
  | 'addFloor'
  | 'labelAr'
  | 'labelEn'
  | 'units'
  | 'addUnit'
  | 'unitNumber'
  | 'floor'
  | 'unitType'
  | 'price'
  | 'priceHint'
  | 'unknown'
  | 'archive'
  | 'restore'
  | 'reason'
  | 'back'
  | 'periods'
  | 'from'
  | 'until'
  | 'history'
  | 'codeInUse'
  | 'numberInUse'
  | 'sameDay'
  | 'correctActivation'
  | 'correctHint'
  | 'notToday'
  | 'branchHasUnits'
  | 'setPrice'
  | 'saved'
  | 'disabled'
  | 'disable'
  | 'enable'
> = {
  en: {
    property: 'Property Setup',
    branches: 'Branches',
    code: 'Code',
    name: 'Name',
    addBranch: 'Add Branch',
    create: 'Create',
    archived: 'Archived',
    operational: 'Operational',
    open: 'Open',
    floors: 'Floors',
    addFloor: 'Add Floor',
    labelAr: 'Label (Arabic)',
    labelEn: 'Label (English)',
    units: 'Units',
    addUnit: 'Add Unit',
    unitNumber: 'Unit Number',
    floor: 'Floor',
    unitType: 'Unit Type',
    price: 'Monthly Reference Price (SAR)',
    priceHint: 'Optional. Leave empty if unknown; never zero. A reference only, never copied into a Booking price.',
    unknown: 'Unknown',
    archive: 'Archive',
    restore: 'Restore',
    reason: 'Reason (optional)',
    back: 'Back to Branches',
    periods: 'Operational periods',
    from: 'From',
    until: 'Until (exclusive)',
    history: 'History',
    codeInUse: 'This code is already used.',
    numberInUse: 'This Unit Number is already used in this Branch.',
    sameDay:
      'The Unit became operational today. If that activation was a mistake, use "Correct today\'s activation" with a reason.',
    correctActivation: "Correct today's activation",
    correctHint: 'Only for an activation made by mistake today, with nothing depending on it. A reason is required.',
    notToday: 'The activation can be corrected only on the day it was made.',
    branchHasUnits: 'Archive or move every operational Unit first.',
    setPrice: 'Set price',
    saved: 'Saved.',
    disabled: 'Disabled',
    disable: 'Disable',
    enable: 'Enable',
  },
  ar: {
    property: 'إعداد العقارات',
    branches: 'الفروع',
    code: 'الرمز',
    name: 'الاسم',
    addBranch: 'إضافة فرع',
    create: 'إنشاء',
    archived: 'مؤرشف',
    operational: 'تشغيلي',
    open: 'فتح',
    floors: 'الطوابق',
    addFloor: 'إضافة طابق',
    labelAr: 'التسمية (عربي)',
    labelEn: 'التسمية (إنجليزي)',
    units: 'الوحدات',
    addUnit: 'إضافة وحدة',
    unitNumber: 'رقم الوحدة',
    floor: 'الطابق',
    unitType: 'نوع الوحدة',
    price: 'السعر المرجعي الشهري (ريال)',
    priceHint: 'اختياري. اتركه فارغًا إذا كان غير معروف؛ لا يكون صفرًا أبدًا. مرجعي فقط ولا يُنسخ إلى سعر الحجز.',
    unknown: 'غير معروف',
    archive: 'أرشفة',
    restore: 'استعادة',
    reason: 'السبب (اختياري)',
    back: 'العودة إلى الفروع',
    periods: 'فترات التشغيل',
    from: 'من',
    until: 'حتى (غير شامل)',
    history: 'السجل',
    codeInUse: 'هذا الرمز مستخدم بالفعل.',
    numberInUse: 'رقم الوحدة هذا مستخدم في هذا الفرع.',
    sameDay: 'أصبحت الوحدة تشغيلية اليوم. إذا كان التفعيل خطأً فاستخدم «تصحيح تفعيل اليوم» مع ذكر السبب.',
    correctActivation: 'تصحيح تفعيل اليوم',
    correctHint: 'فقط لتفعيل تم بالخطأ اليوم ولا يعتمد عليه شيء. السبب مطلوب.',
    notToday: 'لا يمكن تصحيح التفعيل إلا في يوم إجرائه.',
    branchHasUnits: 'أرشف أو انقل جميع الوحدات التشغيلية أولًا.',
    setPrice: 'تعيين السعر',
    saved: 'تم الحفظ.',
    disabled: 'معطّل',
    disable: 'تعطيل',
    enable: 'تفعيل',
  },
};

export function BranchesPage({ session }: { readonly session: CurrentSession }) {
  const client = useApiClient();
  const t = useText(text);
  const queries = useQueryClient();
  const branches = useQuery({
    queryKey: ['property', 'branches', 'all'],
    queryFn: async ({ signal }) => {
      const r = await client.listBranches({ query: { includeArchived: 'true' } }, { signal });
      return readResponse(r).branches;
    },
  });
  const [form, setForm] = useState({ code: '', name: '' });
  const action = useAction();

  async function create(event: FormEvent) {
    event.preventDefault();
    const ok = await action.run((key) => client.createBranch({ headers: { 'idempotency-key': key }, body: form }), {
      code_in_use: t.codeInUse,
    });
    if (ok) {
      setForm({ code: '', name: '' });
      await queries.invalidateQueries({ queryKey: ['property'] });
    }
  }

  return (
    <main>
      <h1>{t.property}</h1>
      <section className="er-card">
        <h2>{t.branches}</h2>
        {branches.isPending ? (
          <Loading />
        ) : branches.isError ? (
          <QueryFailure query={branches} />
        ) : (
          <table className="er-table">
            <thead>
              <tr>
                <th>{t.code}</th>
                <th>{t.name}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(branches.data ?? []).map((b) => (
                <tr key={b.id}>
                  <td className="er-mono">{b.code}</td>
                  <td>
                    <Link to={`/property/${b.id}`}>{b.name}</Link>
                  </td>
                  <td>{b.archived && <Badge tone="off">{t.archived}</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      {session.permissions.includes('property.setup') && session.scope.type === 'all_branches' && (
        <section className="er-card">
          <h2>{t.addBranch}</h2>
          {action.error && <Alert kind="error">{action.error}</Alert>}
          <form onSubmit={create} noValidate>
            <Field id="branch-code" label={t.code}>
              <input
                id="branch-code"
                dir="ltr"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </Field>
            <Field id="branch-name" label={t.name}>
              <input id="branch-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <button className="er-button" type="submit" disabled={action.busy || !form.code || !form.name}>
              {t.create}
            </button>
          </form>
        </section>
      )}
    </main>
  );
}

const UNIT_ACTIONS: Readonly<Record<string, Readonly<Record<'ar' | 'en', string>>>> = {
  created: { en: 'Created', ar: 'أُنشئت' },
  updated: { en: 'Updated', ar: 'عُدّلت' },
  reference_price_changed: { en: 'Reference price changed', ar: 'تغيّر السعر المرجعي' },
  archived: { en: 'Archived', ar: 'أُرشفت' },
  restored: { en: 'Restored', ar: 'استُعيدت' },
};

function useUnitTypes() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['reference', 'unit_type'],
    queryFn: async ({ signal }) => {
      const r = await client.listReferenceValues({ path: { kind: 'unit_type' } }, { signal });
      return readResponse(r).values;
    },
  });
}

export function BranchDetailPage({ session }: { readonly session: CurrentSession }) {
  const { branchId = '' } = useParams();
  const client = useApiClient();
  const t = useText(text);
  const { locale } = useLocale();
  const queries = useQueryClient();
  const action = useAction();
  const can = (key: string) => session.permissions.includes(key);
  const floors = useQuery({
    queryKey: ['property', 'floors', branchId],
    queryFn: async ({ signal }) => {
      const r = await client.listFloors({ path: { branchId }, query: { includeDisabled: 'true' } }, { signal });
      return readResponse(r).floors;
    },
  });
  const units = useQuery({
    queryKey: ['property', 'units', branchId],
    queryFn: async ({ signal }) => {
      const r = await client.listUnits({ query: { branchId, includeArchived: 'true' } }, { signal });
      return readResponse(r).units;
    },
  });
  const unitTypes = useUnitTypes();
  const [floorForm, setFloorForm] = useState({ code: '', ar: '', en: '' });
  const [unitForm, setUnitForm] = useState({ unitNumber: '', floorId: '', unitTypeId: '', price: '' });
  const refresh = () => queries.invalidateQueries({ queryKey: ['property'] });
  const label = (labels: { ar: string; en: string }) => labels[locale];
  const floorName = (id: string) => {
    const floor = floors.data?.find((f) => f.id === id);
    return floor ? label(floor.labels) : '—';
  };

  async function addFloor(event: FormEvent) {
    event.preventDefault();
    const ok = await action.run(
      (key) =>
        client.createFloor({
          path: { branchId },
          headers: { 'idempotency-key': key },
          body: { code: floorForm.code, labels: { ar: floorForm.ar, en: floorForm.en } },
        }),
      { code_in_use: t.codeInUse },
    );
    if (ok) {
      setFloorForm({ code: '', ar: '', en: '' });
      await refresh();
    }
  }

  async function addUnit(event: FormEvent) {
    event.preventDefault();
    const ok = await action.run(
      (key) =>
        client.createUnit({
          headers: { 'idempotency-key': key },
          body: {
            branchId,
            floorId: unitForm.floorId,
            unitNumber: unitForm.unitNumber,
            unitTypeId: unitForm.unitTypeId,
            ...(unitForm.price.trim() ? { monthlyReferencePrice: unitForm.price.trim() } : {}),
          },
        }),
      { unit_number_in_use: t.numberInUse },
    );
    if (ok) {
      setUnitForm({ ...unitForm, unitNumber: '', price: '' });
      await refresh();
    }
  }

  return (
    <main>
      <p>
        <Link to="/property">{t.back}</Link>
      </p>
      {action.error && <Alert kind="error">{action.error}</Alert>}
      <section className="er-card">
        <h2>{t.floors}</h2>
        <QueryFailure query={floors} />
        <table className="er-table">
          <tbody>
            {(floors.data ?? []).map((f) => (
              <tr key={f.id}>
                <td className="er-mono">{f.code}</td>
                <td>{label(f.labels)}</td>
                <td>
                  {f.disabled && <Badge tone="off">{t.disabled}</Badge>}{' '}
                  {can('property.setup') && (
                    <button
                      className="er-link-button"
                      type="button"
                      onClick={async () =>
                        (await action.run((key) =>
                          client.changeFloor({
                            path: { floorId: f.id },
                            headers: { 'idempotency-key': key },
                            body: { disabled: !f.disabled },
                          }),
                        )) && refresh()
                      }
                    >
                      {f.disabled ? t.enable : t.disable}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {can('property.setup') && (
          <form onSubmit={addFloor} noValidate>
            <h3>{t.addFloor}</h3>
            <Field id="floor-code" label={t.code}>
              <input
                id="floor-code"
                dir="ltr"
                value={floorForm.code}
                onChange={(e) => setFloorForm({ ...floorForm, code: e.target.value })}
              />
            </Field>
            <Field id="floor-ar" label={t.labelAr}>
              <input
                id="floor-ar"
                dir="rtl"
                value={floorForm.ar}
                onChange={(e) => setFloorForm({ ...floorForm, ar: e.target.value })}
              />
            </Field>
            <Field id="floor-en" label={t.labelEn}>
              <input
                id="floor-en"
                dir="ltr"
                value={floorForm.en}
                onChange={(e) => setFloorForm({ ...floorForm, en: e.target.value })}
              />
            </Field>
            <button
              className="er-button"
              type="submit"
              disabled={action.busy || !floorForm.code || !floorForm.ar || !floorForm.en}
            >
              {t.create}
            </button>
          </form>
        )}
      </section>

      <section className="er-card">
        <h2>{t.units}</h2>
        {units.isPending ? (
          <Loading />
        ) : units.isError ? (
          <QueryFailure query={units} />
        ) : (
          <table className="er-table">
            <thead>
              <tr>
                <th>{t.unitNumber}</th>
                <th>{t.floor}</th>
                <th>{t.price}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(units.data ?? []).map((u) => (
                <tr key={u.id}>
                  <td className="er-mono">
                    <Link to={`/property/units/${u.id}`}>{u.unitNumber}</Link>
                  </td>
                  <td>{floorName(u.floorId)}</td>
                  <td className="er-mono">{u.monthlyReferencePrice ?? t.unknown}</td>
                  <td>
                    <Badge tone={u.archived ? 'off' : 'ok'}>{u.archived ? t.archived : `✓ ${t.operational}`}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {can('property.setup') && (
          <form onSubmit={addUnit} noValidate>
            <QueryFailure query={unitTypes} />
            <h3>{t.addUnit}</h3>
            <Field id="unit-number" label={t.unitNumber}>
              <input
                id="unit-number"
                dir="ltr"
                value={unitForm.unitNumber}
                onChange={(e) => setUnitForm({ ...unitForm, unitNumber: e.target.value })}
              />
            </Field>
            <Field id="unit-floor" label={t.floor}>
              <select
                id="unit-floor"
                value={unitForm.floorId}
                onChange={(e) => setUnitForm({ ...unitForm, floorId: e.target.value })}
              >
                <option value="" />
                {(floors.data ?? [])
                  .filter((f) => !f.disabled)
                  .map((f) => (
                    <option key={f.id} value={f.id}>
                      {label(f.labels)}
                    </option>
                  ))}
              </select>
            </Field>
            <Field id="unit-type" label={t.unitType}>
              <select
                id="unit-type"
                value={unitForm.unitTypeId}
                onChange={(e) => setUnitForm({ ...unitForm, unitTypeId: e.target.value })}
              >
                <option value="" />
                {(unitTypes.data ?? []).map((ty) => (
                  <option key={ty.id} value={ty.id}>
                    {label(ty.labels)}
                  </option>
                ))}
              </select>
            </Field>
            {can('property.reference_price') && (
              <Field id="unit-price" label={t.price} hint={t.priceHint}>
                <input
                  id="unit-price"
                  dir="ltr"
                  inputMode="decimal"
                  value={unitForm.price}
                  onChange={(e) => setUnitForm({ ...unitForm, price: e.target.value })}
                />
              </Field>
            )}
            <button
              className="er-button"
              type="submit"
              disabled={action.busy || !unitForm.unitNumber || !unitForm.floorId || !unitForm.unitTypeId}
            >
              {t.create}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}

export function UnitPage({ session }: { readonly session: CurrentSession }) {
  const { unitId = '' } = useParams();
  const client = useApiClient();
  const t = useText(text);
  const { locale } = useLocale();
  const queries = useQueryClient();
  const action = useAction();
  const [reason, setReason] = useState('');
  const [price, setPrice] = useState('');
  const detail = useQuery({
    queryKey: ['property', 'unit', unitId],
    queryFn: async ({ signal }) => {
      const r = await client.getUnit({ path: { unitId } }, { signal });
      return readResponse(r);
    },
  });
  if (detail.isPending) return <Loading />;
  if (detail.isError) return <QueryFailure query={detail} />;
  if (!detail.data) return <Alert kind="error">{t.back}</Alert>;
  const { unit, periods, history } = detail.data;
  const can = (key: string) => session.permissions.includes(key);
  const refresh = () => queries.invalidateQueries({ queryKey: ['property'] });

  return (
    <main>
      <p>
        <Link to={`/property/${unit.branchId}`}>{t.back}</Link>
      </p>
      <h1 className="er-mono">{unit.unitNumber}</h1>
      {action.error && <Alert kind="error">{action.error}</Alert>}
      <section className="er-card">
        <p>
          {t.price}: <span className="er-mono">{unit.monthlyReferencePrice ?? t.unknown}</span>
        </p>
        {can('property.reference_price') && (
          <form
            className="er-actions"
            onSubmit={async (e) => {
              e.preventDefault();
              (await action.run((key) =>
                client.setUnitReferencePrice({
                  path: { unitId },
                  headers: { 'idempotency-key': key },
                  body: { monthlyReferencePrice: price.trim() ? price.trim() : null },
                }),
              )) && refresh();
            }}
          >
            <label className="er-visually-hidden" htmlFor="new-price">
              {t.price}
            </label>
            <input
              id="new-price"
              dir="ltr"
              inputMode="decimal"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
            <button className="er-button secondary" type="submit" disabled={action.busy}>
              {t.setPrice}
            </button>
          </form>
        )}
        {can('unit.archive') && (
          <form
            className="er-actions"
            onSubmit={async (e) => {
              e.preventDefault();
              const call = (
                request: Parameters<typeof client.archiveUnit>[0],
              ): Promise<{ readonly status: number; readonly body: unknown }> =>
                unit.archived ? client.restoreUnit(request) : client.archiveUnit(request);
              (await action.run(
                (key) =>
                  call({
                    path: { unitId },
                    headers: { 'idempotency-key': key },
                    body: reason.trim() ? { reason: reason.trim() } : {},
                  }),
                { unit_became_operational_today: t.sameDay },
              )) && refresh();
            }}
          >
            <label className="er-visually-hidden" htmlFor="archive-reason">
              {t.reason}
            </label>
            <input
              id="archive-reason"
              placeholder={t.reason}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <button className={`er-button${unit.archived ? '' : ' danger'}`} type="submit" disabled={action.busy}>
              {unit.archived ? t.restore : t.archive}
            </button>
          </form>
        )}
        {can('unit.archive') && !unit.archived && (
          <form
            className="er-actions"
            onSubmit={async (e) => {
              e.preventDefault();
              (await action.run(
                (key) =>
                  client.correctUnitActivation({
                    path: { unitId },
                    headers: { 'idempotency-key': key },
                    body: { reason: reason.trim() },
                  }),
                { activation_not_today: t.notToday },
              )) && refresh();
            }}
          >
            <span className="er-hint">{t.correctHint}</span>
            <button className="er-button secondary" type="submit" disabled={action.busy || !reason.trim()}>
              {t.correctActivation}
            </button>
          </form>
        )}
      </section>
      <section className="er-card">
        <h2>{t.periods}</h2>
        <table className="er-table">
          <thead>
            <tr>
              <th>{t.from}</th>
              <th>{t.until}</th>
            </tr>
          </thead>
          <tbody>
            {periods.map((p) => (
              <tr key={p.start}>
                <td className="er-mono">{p.start}</td>
                <td className="er-mono">{p.endExclusive ?? t.open}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <section className="er-card">
        <h2>{t.history}</h2>
        <ul>
          {history.map((h) => (
            <li key={`${h.recordedAt}-${h.action}`}>
              <span className="er-mono">{h.effectiveDate}</span> — {UNIT_ACTIONS[h.action]?.[locale] ?? h.action}
              {h.reason ? ` — ${h.reason}` : ''}
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
